/*
 * Custom mega menu (delegated, re-render-safe)
 * The header re-renders its nav (overflow-list recalculation / hydration), which
 * detaches any nodes we'd bind listeners to. So instead of binding to specific
 * nodes, we delegate on `document` and resolve the trigger item at event time —
 * always operating on the live DOM.
 *
 * Each <custom-mega-menu> panel carries data-trigger-url (the destination of the
 * nav item that opens it) and, for panels not yet migrated, data-trigger (the
 * handleised label). Matching is by URL first because labels are translated per
 * locale; the label match remains as an additive fallback, so a wrong or missing
 * trigger URL degrades to the old behaviour instead of disabling the panel.
 *
 * On hover/focus of the matching header nav link the panel opens (positioned under
 * the header) and the theme's native submenu is suppressed via CSS (see the section
 * stylesheet). Closes on mouse-leave, outside click, or Escape.
 */
(function () {
  'use strict';

  function handleize(value) {
    return (value || '')
      .toLowerCase()
      .trim()
      // Liquid's handleize transliterates accented Latin letters ('Café' →
      // 'cafe'), so decompose and drop the combining marks before the ASCII
      // strip — otherwise the two sides disagree and the fallback never matches.
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Reduce a URL or path to a locale-independent, comparable pathname.
   * The nav link's href carries the Shopify locale root (/ko/, /ja/…) while the
   * trigger URL setting may carry none, or one from whichever storefront the
   * merchant was editing — so both sides run through this.
   *
   * `host` is the current storefront host; values pointing anywhere else are not
   * comparable and return ''. Omitting it accepts relative values only.
   * Returns '' for anything with no comparable path, so callers can distinguish
   * "nothing to match on" from the home page ('/').
   */
  function normalisePath(value, root, host) {
    if (!value) return '';
    var raw = String(value).trim();
    // A value with no path of its own ('#', '#top', '?sort=title') would resolve
    // against the base to '/' and collide with a home-page trigger URL.
    if (!raw || raw.charAt(0) === '#' || raw.charAt(0) === '?') return '';
    var origin = 'http://' + (host || 'localhost');
    var url;
    try {
      url = new URL(raw, origin);
    } catch (e) {
      return '';
    }
    // Off-site links and non-http URIs (shopify://, mailto:) share no path space
    // with this storefront; comparing pathnames alone would match them wrongly.
    if (url.host !== (host || 'localhost')) return '';
    // Case-fold: the label match this replaced lowercased both sides, and the
    // url setting accepts a hand-pasted, arbitrarily cased path.
    var path = url.pathname.toLowerCase();
    var prefix = (root || '/').toLowerCase();
    if (prefix !== '/') {
      // Shopify renders the storefront root without a trailing slash, so a
      // home-page nav item on the Korean store arrives as '/ko', not '/ko/'.
      var bare = prefix.slice(0, -1);
      if (path === bare) {
        path = '/';
      } else if (path.indexOf(prefix) === 0) {
        path = '/' + path.slice(prefix.length);
      }
    }
    // A trigger URL saved while editing a translated storefront keeps that
    // locale's prefix, which the root strip above cannot remove when we are
    // viewing a different locale. Only a language-code segment with more path
    // after it is stripped, so '/kobe/...' survives.
    path = path.replace(/^\/[a-z]{2}(-[a-z]{2,4})?(?=\/)/, '');
    path = path.replace(/\/+$/, '');
    return path || '/';
  }

  /**
   * Resolve the panel a nav link should open. Pure so it can be unit tested:
   * `panels` is the collected list, `href` and `label` come from the live link.
   * URL match first, then the legacy label. The label pass is deliberately not
   * gated on the panel lacking a trigger URL — a URL that does not match the nav
   * item must degrade to the old behaviour, not switch the panel off.
   */
  function findPanel(panels, href, label, root, host) {
    var path = normalisePath(href, root, host);
    var i;
    if (path) {
      for (i = 0; i < panels.length; i++) {
        if (panels[i].triggerUrl && normalisePath(panels[i].triggerUrl, root, host) === path) {
          return panels[i];
        }
      }
    }
    // Both sides must be non-empty: a translated label handleises to '' and would
    // otherwise match every panel whose trigger is also ''.
    var h = handleize(label);
    if (!h) return null;
    for (i = 0; i < panels.length; i++) {
      if (panels[i].trigger && panels[i].trigger === h) return panels[i];
    }
    return null;
  }

  /**
   * Drop panels whose element has been detached (a theme-editor section re-render
   * replaces the node). A stale entry sits at a lower index than its replacement
   * and keeps winning the match, so `is-open` lands on an off-document node.
   * Mutates `panels` and returns the removed entries so callers can clear timers.
   */
  function prunePanels(panels) {
    var removed = [];
    for (var i = panels.length - 1; i >= 0; i--) {
      if (!panels[i].el || !panels[i].el.isConnected) {
        removed.push(panels.splice(i, 1)[0]);
      }
    }
    return removed;
  }

  // Guard the DOM engine so the file can also be required under Node (for the
  // pure-utility unit tests) where `window`/`document` do not exist.
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  var panels = []; // { el, trigger, triggerUrl, timer, link }

  function collect() {
    // A section re-render replaces the panel node, so clear out the detached
    // entries before adding the fresh ones (and drop their pending timers).
    prunePanels(panels).forEach(cancelClose);
    document.querySelectorAll('custom-mega-menu').forEach(function (el) {
      if (el.dataset.cmmReady === '1') return;
      var trigger = el.getAttribute('data-trigger');
      var triggerUrl = el.getAttribute('data-trigger-url');
      // A panel is usable with either identifier. Note `trigger` is '' on
      // translated locales (the label setting handleises to nothing), so the
      // URL is the only thing keeping the panel alive there.
      if (!trigger && !triggerUrl) return;
      el.dataset.cmmReady = '1';
      panels.push({ el: el, trigger: trigger, triggerUrl: triggerUrl, timer: null, link: null });
    });
  }

  function getHeader() {
    return (
      document.querySelector('#header-component') ||
      document.querySelector('.header-section') ||
      document.querySelector('.header')
    );
  }

  function localeRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function panelForLink(link) {
    var href = link.getAttribute ? link.getAttribute('href') : '';
    var label = link.querySelector('.menu-list__link-title') || link;
    return findPanel(panels, href, label.textContent, localeRoot(), window.location.host);
  }

  function panelByEl(el) {
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].el === el) return panels[i];
    }
    return null;
  }

  function position(p) {
    var header = getHeader();
    if (header) {
      p.el.style.top = Math.max(0, header.getBoundingClientRect().bottom) + 'px';
    }
  }

  function cancelClose(p) {
    if (p.timer) {
      clearTimeout(p.timer);
      p.timer = null;
    }
  }

  function closeNow(p) {
    p.el.classList.remove('is-open');
    if (p.link) {
      p.link.classList.remove('menu-list__link--active');
      p.link.setAttribute('aria-expanded', 'false');
    }
  }

  function deferClose(p) {
    cancelClose(p);
    p.timer = setTimeout(function () {
      closeNow(p);
    }, 180);
  }

  function open(p, link) {
    cancelClose(p);
    // Only one custom panel open at a time.
    panels.forEach(function (other) {
      if (other !== p) closeNow(other);
    });
    if (link) {
      p.link = link;
      link.classList.add('menu-list__link--active');
      link.setAttribute('aria-expanded', 'true');
      var item = link.closest('.menu-list__list-item');
      if (item) item.classList.add('has-custom-mega-menu');
    }
    position(p);
    p.el.classList.add('is-open');
  }

  function onOver(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var link = target.closest('.menu-list__link');
    if (link) {
      var p = panelForLink(link);
      if (p) open(p, link);
      return;
    }

    var panelEl = target.closest('custom-mega-menu');
    if (panelEl) {
      var pp = panelByEl(panelEl);
      if (pp) cancelClose(pp);
    }
  }

  function onOut(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var link = target.closest('.menu-list__link');
    if (link) {
      var p = panelForLink(link);
      if (p) deferClose(p);
      return;
    }

    var panelEl = target.closest('custom-mega-menu');
    if (panelEl) {
      var pp = panelByEl(panelEl);
      if (pp) deferClose(pp);
    }
  }

  function onFocusIn(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var link = target.closest('.menu-list__link');
    if (link) {
      var p = panelForLink(link);
      if (p) {
        open(p, link);
        return;
      }
    }

    if (target.closest('custom-mega-menu')) return;

    // Focus moved outside any trigger/panel — close everything.
    panels.forEach(deferClose);
  }

  function onKeydown(event) {
    if (event.key === 'Escape') panels.forEach(closeNow);
  }

  function onClick(event) {
    panels.forEach(function (p) {
      if (!p.el.classList.contains('is-open')) return;
      if (p.el.contains(event.target)) return;
      var item = p.link ? p.link.closest('.menu-list__list-item') : null;
      if (item && item.contains(event.target)) return;
      closeNow(p);
    });
  }

  function reposition() {
    panels.forEach(function (p) {
      if (p.el.classList.contains('is-open')) position(p);
    });
  }

  function init() {
    collect();
  }

  document.addEventListener('mouseover', onOver);
  document.addEventListener('mouseout', onOut);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('click', onClick);
  window.addEventListener('resize', reposition);
  window.addEventListener('scroll', reposition, { passive: true });

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
  document.addEventListener('shopify:section:load', init);
  } // end DOM engine guard

  // CommonJS export shim (for node --test; harmless in browser).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      handleize: handleize,
      normalisePath: normalisePath,
      findPanel: findPanel,
      prunePanels: prunePanels
    };
  }
})();
